import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  PortalDsAlert,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { cn } from 'src/lib/utils';
import {
  formatBytesProcessed,
  formatRunDuration,
  formatRowCount,
} from '../helpers/data-transfers.helpers';
import type { DataTransferRun } from '../types/data-transfers.types';
import { DataTransferStatusBadge } from './data-transfer-status-badge';

function RunErrorMessage({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = message.length > 240;
  const displayText = expanded || !isLong ? message : `${message.slice(0, 240)}…`;

  return (
    <div className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700">
      <p className="whitespace-pre-wrap break-words">
        {displayText}
      </p>
      {isLong ? (
        <button
          type="button"
          className="mt-2 text-xs font-medium underline-offset-2 hover:underline"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}

interface DataTransferRunSheetProps {
  open: boolean;
  run: DataTransferRun | null | undefined;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DataTransferRunSheet({
  open,
  run,
  isLoading = false,
  onOpenChange,
}: DataTransferRunSheetProps) {
  const triggeredByLabel = run?.triggered_by?.name
    ?? run?.triggered_by?.email
    ?? null;

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:!w-[70vw] sm:!max-w-none"
      >
        <div className={portalDs.sheetHeader}>
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className={portalDs.sheetTitle}>
              {run?.transfer_name ?? 'Run detail'}
            </SheetTitle>
            <SheetDescription className={portalDs.sheetDescription}>
              Inspect timing, row counts, and error details for this transfer run.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 space-y-8 px-6 py-5">
          {isLoading ? (
            <div
              className={cn('flex items-center gap-2 py-8', portalDs.bodySm)}
              role="status"
            >
              <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
              Loading run detail…
            </div>
          ) : null}

          {!isLoading && run ? (
            <>
              <section className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <DataTransferStatusBadge status={run.status} />
                  <span className={cn(portalDs.bodySm, 'capitalize')}>
                    {run.trigger_type}
                  </span>
                  {triggeredByLabel ? (
                    <span className={portalDs.bodySm}>
                      by
                      {' '}
                      {triggeredByLabel}
                    </span>
                  ) : null}
                </div>

                <dl className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className={portalDs.label}>
                      Client
                    </dt>
                    <dd className={portalDs.bodySm}>
                      {run.client_title}
                    </dd>
                  </div>
                  <div>
                    <dt className={portalDs.label}>
                      Run ID
                    </dt>
                    <dd className="font-mono text-xs text-gray-700">
                      {run.id}
                    </dd>
                  </div>
                  <div>
                    <dt className={portalDs.label}>
                      Started
                    </dt>
                    <dd className={portalDs.bodySm}>
                      {run.started_at ? formatDisplayDateTime(run.started_at) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className={portalDs.label}>
                      Finished
                    </dt>
                    <dd className={portalDs.bodySm}>
                      {run.finished_at ? formatDisplayDateTime(run.finished_at) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className={portalDs.label}>
                      Duration
                    </dt>
                    <dd className={portalDs.bodySm}>
                      {formatRunDuration(run.started_at, run.finished_at)}
                    </dd>
                  </div>
                  <div>
                    <dt className={portalDs.label}>
                      Rows written
                    </dt>
                    <dd className={portalDs.bodySm}>
                      {formatRowCount(run.row_count)}
                    </dd>
                  </div>
                  <div>
                    <dt className={portalDs.label}>
                      Bytes processed
                    </dt>
                    <dd className={portalDs.bodySm}>
                      {run.bytes_processed != null
                        ? formatBytesProcessed(run.bytes_processed)
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className={portalDs.label}>
                      Created
                    </dt>
                    <dd className={portalDs.bodySm}>
                      {formatDisplayDateTime(run.created_at)}
                    </dd>
                  </div>
                </dl>
              </section>

              {run.status === 'failed' && run.error_message ? (
                <section className="space-y-3">
                  <h3 className={portalDs.sectionTitle}>
                    Error
                  </h3>
                  <RunErrorMessage message={run.error_message} />
                </section>
              ) : null}

              {run.status === 'running' || run.status === 'pending' ? (
                <PortalDsAlert variant="info">
                  This run is still in progress. The detail view refreshes automatically.
                </PortalDsAlert>
              ) : null}
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
